import type { AuthConfig } from '@myosh/myosh-login';
import { useAuth, useUser } from '@myosh/myosh-login';
import { Loader } from '@myosh/odin-components';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import { useHydrateBackgroundJobs } from '../../hooks/use-hydrate-background-jobs';
import useProfileData from '../../hooks/use-profile-data';
import usePrefetchLayoutData from '../../redux/prefetch/use-prefetch-layout-data';
import { useGetCurrentSchemaInfoQuery, useGetMetaDataQuery } from '../../redux/services/api';
import { useAddUserToNotificationsGroupMutation, useGetCompanyLogoQuery } from '../../redux/services/user';
import ChangePasswordModal from './components/change-password-modal.component';
import OdinSideNavigation from './components/odin-side-navigation.component';
import useNavigationItemClickHandler from './hooks/use-click-handler';
import useNavigationItems from './hooks/use-navigation-items';

const Layout = () => {
  const { state: userState } = useUser();
  const isSuperUser = userState.user?.superuser === 'true';
  const isDefaultIdp = userState.isDefaultIdp;

  const [isCollapsed, setCollapsed] = useState(false);

  const { setAuthConfig } = useAuth();

  const { t } = useTranslation();

  useHydrateBackgroundJobs();
  usePrefetchLayoutData();

  const [addUserToNotifcationGroup] = useAddUserToNotificationsGroupMutation();

  const itemClickHandler = useNavigationItemClickHandler(setCollapsed);
  const {
    navigationItems,
    isLoading: isLoadingNavigationItems,
    isError: isNavigationItemsError,
  } = useNavigationItems(itemClickHandler);

  const { data: { logout_url: logoutUrl } = {}, isLoading: isLoadingSchemaInfo } = useGetCurrentSchemaInfoQuery();
  const { profileData: { user: userData } = {}, isLoadingUser, isFetchingUser } = useProfileData();

  const { data: companyLogo, isLoading: isLogoLoading } = useGetCompanyLogoQuery(userData?.id ?? 0, {
    skip: !userData || !userData.id || userData.superUser,
  });

  const { data: metaData, isLoading: isLoadingMetaData } = useGetMetaDataQuery();

  useEffect(() => {
    if (!isSuperUser) {
      addUserToNotifcationGroup();
    }
  }, [isSuperUser]);

  useEffect(() => {
    if (isLoadingSchemaInfo || isLoadingMetaData) return;

    let authConfig: Partial<AuthConfig> = {};

    const frontendSystem = metaData?.find((data) => data.key === 'frontend_system');
    if (frontendSystem && frontendSystem.value) {
      authConfig = { ...authConfig, frontendSystem: frontendSystem.value };
    }

    if (!isDefaultIdp && logoutUrl) {
      authConfig = { ...authConfig, logoutUrl };
    }

    setAuthConfig(authConfig);
  }, [isDefaultIdp, logoutUrl, isLoadingSchemaInfo, setAuthConfig, metaData, isLoadingMetaData]);

  const contentStyles = clsx('flex-grow pl-1 custom-scroll overflow-auto', {
    'sm:visible max-sm:invisible': !isCollapsed,
  });

  const isLoading = isLoadingNavigationItems || isLoadingUser || isLogoLoading;

  return (
    <div className="flex h-full w-full overflow-hidden">
      <OdinSideNavigation
        loading={isLoading}
        setItemCollapsed={setCollapsed}
        isItemCollapsed={isCollapsed}
        items={navigationItems}
        companyLogo={{ url: companyLogo, isLoading: isLogoLoading }}
        isNavigationItemsError={isNavigationItemsError}
        hasUserAdminAccess={userData?.hasAdminAccess}
      />
      <main className={contentStyles}>
        <div className="relative mx-auto flex h-full w-full flex-col items-center justify-center overflow-hidden max-sm:pl-5 sm:pl-10">
          {isLoadingUser ? <Loader title={t('loading-data')} /> : <Outlet />}
        </div>
      </main>

      <ChangePasswordModal
        visible={Boolean(userData?.passwordChangeRequired)}
        isFetchingPasswordCheck={isFetchingUser}
      />
    </div>
  );
};

export default Layout;
